import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

// ! FIX ME - Change these types to a better place

export type TUser = {
  id: number;
  name: string;
  email: string;
  acronym: string;
  profileId: number;
  agencyId: number;
};

export type TUserNavigation = {
  to: string;
  label: string;
  onClick?: () => void;
};

export type TBreadcrumb = {
  to: string;
  label: string;
};

// *********************************************************************
// * LayoutContext
// *********************************************************************
export type TSectionTab = {
  key: string;
  label: string | ReactNode;
  url?: string;
};

export type TNotificationItem = {
  badgeNumber?: number;
  label: string;
  onClick: () => void;
  icon?: ReactNode;
};

export type TNotificationSection = {
  title?: string;
  notificationsItems: TNotificationItem[];
};

export type TLayoutContext = {
  homeUrl?: string;
  brandLogo?: ReactNode;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  hideHeader?: boolean;
  user?: TUser;
  userNavigation?: TUserNavigation[];
  notificationsNavigation?: TUserNavigation[];
  breadcrumbs?: TBreadcrumb[];
  sidebar?: ReactNode;
  pageHeading?: ReactNode;
  notificationsSections?: TNotificationSection[];

  tabs?: {
    elements: TSectionTab[];
    current: string;
    setCurrent: React.Dispatch<React.SetStateAction<string>>;
  };
  hideTabs?: boolean;

  viewAll?: boolean;
  setViewAll?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const LayoutContext = React.createContext<TLayoutContext | null>(null);

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error(
      'useLayoutContext must be used within a LayoutContext.Provider',
    );
  }

  return context;
};

// *********************************************************************
// * LayoutControlContext
// *********************************************************************

export type TLayoutControlContext = {
  sidebarOpen: boolean;
  setSidebarOpen: any;
};

export const LayoutControlContext =
  React.createContext<TLayoutControlContext | null>(null);

export const LayoutControlContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // For testing purposes matchMedia needs to exist
  if (typeof window !== 'undefined') {
    window.matchMedia =
      window.matchMedia ||
      function () {
        return {
          matches: false,
          addListener: function () {},
          removeListener: function () {},
        };
      };
  }

  const isDesktop = useMediaQuery('(min-width: 768px)');

  // using useMediaQuery was false in the first call causing the sidebar to be closed, using window.innerWidth to set initial state
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // using use effect to set update the sidebarOpen state
  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  return (
    <LayoutControlContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      {children}
    </LayoutControlContext.Provider>
  );
};

export const useLayoutControlContext = () => {
  const context = useContext(LayoutControlContext);

  if (!context) {
    throw new Error(
      'useLayoutControlContext must be used within a LayoutControlContextProvider',
    );
  }

  return context;
};
